/**
 * 加密存储工具类 - 用于安全地存储敏感数据到 localStorage
 * Encrypted storage utility class - for securely storing sensitive data in localStorage
 */
export class CryptoStorage {
  private static readonly SECRET_KEY = 'eb-ssms-portal-2024-secret-key-for-encryption';

  /**
   * 简单的字符串加密（基于 Base64 和异或运算）
   * Simple string encryption based on Base64 and XOR operation
   */
  private static encrypt(text: string): string {
    const key = this.SECRET_KEY;
    // 先将 Unicode 字符串转为 UTF-8 字节串（每个字符 code point 在 0-255 范围内）
    const utf8 = unescape(encodeURIComponent(text));
    let encrypted = '';
    for (let i = 0; i < utf8.length; i++) {
      encrypted += String.fromCharCode(utf8.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(encrypted); // Base64 编码
  }

  /**
   * 简单的字符串解密
   * Simple string decryption
   */
  private static decrypt(encryptedText: string): string {
    try {
      const key = this.SECRET_KEY;
      const decoded = atob(encryptedText); // Base64 解码
      let decrypted = '';
      for (let i = 0; i < decoded.length; i++) {
        decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      // 将 UTF-8 字节串还原为 Unicode 字符串
      return decodeURIComponent(escape(decrypted));
    } catch {
      // 如果解密失败，可能是未加密的数据，直接返回
      return encryptedText;
    }
  }

  /**
   * 加密数据并存储到 localStorage
   * Encrypt data and store in localStorage
   */
  static setItem(key: string, value: string): void {
    try {
      const encrypted = this.encrypt(value);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Failed to encrypt and store data:', error);
    }
  }

  /**
   * 从 localStorage 获取并解密数据
   * Get and decrypt data from localStorage
   */
  static getItem(key: string): string | null {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) {
        return null;
      }

      return this.decrypt(encrypted);
    } catch (error) {
      console.error('Failed to decrypt data:', error);
      return null;
    }
  }

  /**
   * 从 localStorage 删除数据
   * Remove data from localStorage
   */
  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}
